import { OnboardingService } from '../api/services/onboardingService';
import { evaluateVisibilityRule, type VisibilityNode } from '../../engine/runtime/visibility/dsl';
import type { GuidanceNoteRecord, IncomeTriggerRecord } from '../api/types';
import { findSchemaFieldByApiName, getFieldLabelByApiName } from './budgetFieldMapping';

export async function processAddressVerification(userId: number) {
  // Check if verification was just completed for this user (prevents redirect loop after successful verification)
  try {
    const addressVerified = localStorage.getItem('addressVerified');
    if (addressVerified) {
      const parsed = JSON.parse(addressVerified) as { userId: number; lcode: string; timestamp: number };
      const isRecent = Date.now() - parsed.timestamp < 60 * 1000; // 1 minute grace period
      if (parsed.userId === userId && isRecent) {
        console.log('processAddressVerification - Address verification just completed, skipping redirect');
        // Clear the flag so subsequent calls check the API
        localStorage.removeItem('addressVerified');
        // Clear browser query params before navigating to avoid carrying over old params
        if (window.location.search) {
          window.history.replaceState(null, '', window.location.pathname + window.location.hash);
        }
        return '/new-onboarding?step=0';
      }
    }
  } catch (e) {
    console.warn('Failed to check addressVerified flag:', e);
  }

  // 1. Call API to determine if address verification is required
  const resp = await OnboardingService.getCustomerVerificationDetails(userId);

  // The API can return either of the following shapes:
  // - { verifyCustomer: 'Y' | 'N', noOfAttempts: number }  // 'Y' means verification REQUIRED
  // - { customerVerified: 'Y' | 'N' }                      // 'Y' means already VERIFIED

  const getStringProp = (obj: unknown, key: string): string | undefined => {
    if (!obj || typeof obj !== 'object') return undefined;
    const v = (obj as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : undefined;
  };

  const verifyCustomer = (getStringProp(resp, 'verifyCustomer') || '').trim().toUpperCase();
  const customerVerified = (getStringProp(resp, 'customerVerified') || '').trim().toUpperCase();

  // If verification is NOT required, proceed to onboarding and clear any pending lcode markers
  if (verifyCustomer === 'N' || customerVerified === 'Y') {
    try {
      localStorage.removeItem('pendingLcode');
      localStorage.removeItem('pendingLcode_temp');
    } catch {}
    // Clear browser query params before navigating to avoid carrying over old params
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    }
    return '/new-onboarding?step=0';
  }

  // If verification IS required (new or legacy), route to verification
  if (verifyCustomer === 'Y' || customerVerified === 'N') {
    return '/verify-address';
  }

  // Safe default: do not force verification unless API explicitly says so
  return '/new-onboarding?step=0';
}

// =============================================================================
// PROMPT SYSTEM FOR BUDGET VALIDATION
// =============================================================================

/**
 * Prompt types matching the 6 different prompt scenarios
 */
export type PromptType = 
  | 'profile_to_enter' 
  | 'profile_to_remove' 
  | 'limit_without_values' 
  | 'limit_with_values' 
  | 'universal_credit_dependent' 
  | 'whole_section';

/**
 * Prompt configuration as defined in JSON schema files
 */
export interface PromptConfig {
  id: string;
  type: PromptType;
  priority: number;
  visibilityCondition: VisibilityNode;
  messageTemplate?: string;  // Optional override of default message
  clickableSegments: Array<{
    placeholder: string;          // e.g., "{{fieldLabel}}" or "{{profileFieldLabel}}"
    targetApiFieldName: string;   // e.g., "customerEmploymentStatus", "basicIncome"
    action: 'openFieldEdit' | 'openNotes';
  }>;
}

/**
 * Runtime context provided when evaluating prompts
 */
export interface PromptContext {
  schemaFieldName: string;        // e.g., "takeHomeSalary"
  apiFieldName: string;           // e.g., "basicIncome"
  categoryKey: string;            // e.g., "salary", "benefits"
  fieldLabel: string;             // e.g., "What is your monthly take-home pay from employment or self-employment?"
  currentValue: any;              // Current field value
  formData: Record<string, any>;  // All form values
  triggers: IncomeTriggerRecord[]; // Trigger data from API
  guidanceNotes?: GuidanceNoteRecord[];
}

/**
 * Resolved prompt with all placeholders replaced
 */
export interface ResolvedPrompt {
  id: string;
  type: PromptType;
  priority: number;
  message: string;
  clickableSegments: Array<{
    text: string;
    targetApiFieldName: string;
    targetSchemaFieldName: string;
    targetSchemaKey: string;
    action: 'openFieldEdit' | 'openNotes';
  }>;
}

const NOTE_LABEL = 'add a note';

/**
 * Default message templates for each prompt type
 */
const PROMPT_MESSAGES: Record<PromptType, string> = {
  profile_to_enter: 
    "It looks like you've missed entering {{fieldLabel}}. Please review or {{noteLabel}}.",
  
  profile_to_remove: 
    "Are you sure this is correct? You have selected your {{profileFieldLabel}} as {{profileFieldValue}}. Please review or {{noteLabel}}.",
  
  limit_without_values: 
    "Please review {{fieldLabel}} to make sure you have entered the correct amount, or {{noteLabel}}.",
  
  limit_with_values: 
    "Based on what you've selected, the most you could get for {{fieldLabel}} is {{calculatedLimit}} each month. Please review or {{noteLabel}}.",
  
  universal_credit_dependent: 
    "Are you sure you want to add {{fieldLabel}} as you've selected you receive Universal Credit? Please review or {{noteLabel}}.",
  
  whole_section: 
    "Based on the personal circumstances that you have provided, there are no questions in this section for you to answer. Please click on the {{profileFieldLabel}} button to proceed or {{noteLabel}}."
};

/**
 * Main function to evaluate prompts for a field and return the highest priority match
 * 
 * @param prompts - Array of prompt configurations from JSON schema
 * @param context - Runtime context with field data and form state
 * @returns Resolved prompt with populated placeholders, or null if no prompt applies
 */
export function getPrompt(
  prompts: PromptConfig[] | undefined,
  context: PromptContext
): ResolvedPrompt | null {
  
  if (!prompts || prompts.length === 0) return null;
  
  // Augment formData with triggers for visibility evaluation
  const augmentedData = {
    ...context.formData,
    _triggers: context.triggers,
    _currentFieldValue: context.currentValue
  };
  
  // Filter applicable prompts and sort by priority (lower number = higher priority)
  const applicablePrompts = prompts
    .filter(prompt => {
      try {
        return evaluateVisibilityRule(prompt.visibilityCondition, augmentedData);
      } catch (error) {
        console.warn('Error evaluating prompt visibility:', error);
        return false;
      }
    })
    .sort((a, b) => a.priority - b.priority);
  
  if (applicablePrompts.length === 0) return null;
  
  // Take highest priority prompt (extra guard for strict indexed access)
  const selectedPrompt = applicablePrompts[0];
  if (!selectedPrompt) {
    return null;
  }
  
  // Resolve message with placeholders
  const message = resolvePromptMessage(
    selectedPrompt.type,
    selectedPrompt.messageTemplate || PROMPT_MESSAGES[selectedPrompt.type],
    context
  );
  
  // Resolve clickable segments
  const segments = resolveClickableSegments(
    selectedPrompt.clickableSegments,
    context
  );
  
  return {
    id: selectedPrompt.id,
    type: selectedPrompt.type,
    priority: selectedPrompt.priority,
    message,
    clickableSegments: segments
  };
}

/**
 * Convert camelCase to UPPER_SNAKE_CASE
 * e.g., 'basicIncome' -> 'BASIC_INCOME'
 */
function camelToUpperSnake(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .toUpperCase()
    .replace(/^_/, '');
}

/**
 * Resolve message template with runtime values
 */
function resolvePromptMessage(
  type: PromptType,
  template: string,
  context: PromptContext
): string {
  let message = template;
  
  // Replace {{fieldLabel}}
  message = message.replace(/\{\{fieldLabel\}\}/g, context.fieldLabel);

  // Replace {{noteLabel}}
  message = message.replace(/\{\{noteLabel\}\}/g, NOTE_LABEL);
  
  // Replace {{calculatedLimit}} for limit prompts
  if (type === 'limit_with_values' && context.triggers) {
    // Convert camelCase to UPPER_SNAKE_CASE to match API columnName format
    const upperSnakeCase = camelToUpperSnake(context.apiFieldName);
    
    const trigger = context.triggers.find(
      t => t.columnName === upperSnakeCase || t.fieldName === context.apiFieldName
    );
    
    if (trigger?.upperLimit) {
      message = message.replace(
        /\{\{calculatedLimit\}\}/g,
        `£${trigger.upperLimit.toLocaleString()}`
      );
    }
  }
  
  // Replace {{currentValue}}
  if (context.currentValue) {
    message = message.replace(/\{\{currentValue\}\}/g, String(context.currentValue));
  }
  
  // Note: {{profileFieldLabel}} and {{profileFieldValue}} are resolved in segments
  
  return message;
}

/**
 * Resolve clickable segments with runtime context
 */
function resolveClickableSegments(
  segments: PromptConfig['clickableSegments'],
  context: PromptContext
): ResolvedPrompt['clickableSegments'] {
  
  return segments.map(segment => {
    // Find schema field name and key from API field name
    const { schemaFieldName, schemaKey } = findSchemaFieldByApiName(
      segment.targetApiFieldName,
      context.categoryKey
    );
    
    // Resolve placeholder text
    let displayText = segment.placeholder;
    
    if (segment.placeholder === '{{fieldLabel}}') {
      displayText = context.fieldLabel;
    } else if (segment.placeholder === '{{noteLabel}}') {
      displayText = NOTE_LABEL;
    } else if (segment.placeholder === '{{profileFieldLabel}}') {
      displayText = getFieldLabelByApiName(segment.targetApiFieldName);
    } else if (segment.placeholder.startsWith('{{') && segment.placeholder.endsWith('}}')) {
      // Dynamic placeholder - try to resolve
      const fieldName = segment.placeholder.slice(2, -2);
      displayText = getFieldLabelByApiName(segment.targetApiFieldName) || fieldName;
    } else {
      // Static text like "add a note"
      displayText = segment.placeholder;
    }
    
    return {
      text: displayText,
      targetApiFieldName: segment.targetApiFieldName,
      targetSchemaFieldName: schemaFieldName,
      targetSchemaKey: schemaKey,
      action: segment.action
    };
  });
}